import type { ModuleId } from "../auth/config.ts";

/**
 * What an export run should cover, decided in the Scan step before anything is
 * downloaded (ARCHITECTURE.md §15 — the two-step scan → select → export flow).
 *
 * A plan is two independent filters:
 *  - a per-module date range (skip items outside it) — only Teams and Mail
 *    offer one, so only those keys are ever set;
 *  - a per-module container selection (which chats, folders, calendars, …).
 *
 * Both are stored on the run (RunRecord.options) so a resume honours the same
 * choices without re-asking. Pure and dependency-light so the predicates can
 * be unit-tested and reused by every module's export.
 */

/** Date-only bounds (`YYYY-MM-DD`), each inclusive, either side optional. */
export interface DateRange {
    after?: string;
    before?: string;
}

/**
 * "all" means every container in the module (the default when the user didn't
 * narrow it); otherwise only the listed container ids.
 */
export type ContainerSelection = "all" | { readonly ids: readonly string[] };

export interface ExportPlan {
    selection: Partial<Record<ModuleId, ContainerSelection>>;
    /** Per-module date bounds; only Teams and Mail are ever present. */
    dateRanges: Partial<Record<ModuleId, DateRange>>;
}

export const EMPTY_PLAN: ExportPlan = { selection: {}, dateRanges: {} };

const DAY_MS = 24 * 60 * 60 * 1000;

function startOfDay(dateOnly: string): number {
    return Date.parse(`${dateOnly}T00:00:00Z`);
}

/**
 * Whether an item's timestamp falls within the range. An item with no usable
 * date is always kept — a date filter should never silently drop something it
 * can't place, only things it can prove are outside the window.
 *
 * `after` is inclusive from the start of that day; `before` is inclusive
 * through the end of that day, so "between 1 Mar and 1 Mar" keeps everything
 * dated on 1 March.
 */
export function withinRange(iso: string | undefined | null, range: DateRange): boolean {
    if (!iso) {
        return true;
    }
    const t = Date.parse(iso);
    if (Number.isNaN(t)) {
        return true;
    }
    if (range.after) {
        const from = startOfDay(range.after);
        if (!Number.isNaN(from) && t < from) {
            return false;
        }
    }
    if (range.before) {
        const through = startOfDay(range.before) + DAY_MS; // exclusive end of that day
        if (!Number.isNaN(through) && t >= through) {
            return false;
        }
    }
    return true;
}

/** Whether a specific container id is included by a module's selection. */
export function isContainerSelected(
    selection: ContainerSelection | undefined,
    id: string
): boolean {
    if (!selection || selection === "all") {
        return true;
    }
    return selection.ids.includes(id);
}

/**
 * For OneDrive, whose selection ids are folder *paths*: a file is included
 * when it sits inside a selected folder or any descendant of one. A file at
 * the drive root (no folder) is only included when the whole module is "all",
 * since there is no root node to select.
 */
export function isPathSelected(
    folderSegments: readonly string[],
    selection: ContainerSelection | undefined
): boolean {
    if (!selection || selection === "all") {
        return true;
    }
    const selected = new Set(selection.ids);
    for (let depth = folderSegments.length; depth >= 1; depth -= 1) {
        if (selected.has(folderSegments.slice(0, depth).join("/"))) {
            return true;
        }
    }
    return false;
}

/**
 * The filters a paging/delta walk applied while it ran. Items a filter skipped
 * are deliberately left unmarked (so a wider run can still take them), but
 * the cursor itself moves past them — continuing from that cursor later would
 * never list them again. Recording the scope on the cursor lets the next run
 * tell whether continuing is safe or the container must be re-listed.
 *
 * Only filters applied *inside* a cursor matter: the date window (Teams, Mail,
 * Calendar, OneDrive) and OneDrive's folder-path selection, which filters
 * within its single drive-wide cursor. Other modules' selections pick whole
 * containers, each with its own cursor, so an unselected container's cursor
 * simply never moves.
 */
export interface WalkScope {
    /** Date window applied; absent means every date. */
    dateRange?: DateRange;
    /** OneDrive folder paths applied; absent means the whole drive. */
    paths?: readonly string[];
}

/** Normalised scope for a walk: empty filters are dropped so "no filter" has one form. */
export function walkScope(dateRange?: DateRange, pathSelection?: ContainerSelection): WalkScope {
    const scope: WalkScope = {};
    const after = dateRange?.after || undefined;
    const before = dateRange?.before || undefined;
    if (after || before) {
        scope.dateRange = { ...(after ? { after } : {}), ...(before ? { before } : {}) };
    }
    if (pathSelection && pathSelection !== "all") {
        scope.paths = [...pathSelection.ids].sort();
    }
    return scope;
}

/**
 * Whether a walk under `previous` already covered everything `current` asks
 * for, so continuing from its cursor cannot miss an item. Widening a date
 * window or a OneDrive folder selection is not covered: the container must be
 * re-listed (already-exported items are still skipped, so that costs listing
 * calls, not downloads). Narrowing is covered.
 *
 * A cursor written before scopes were recorded (`previous` undefined) is
 * assumed covered: its actual filter is unknowable, and re-listing every
 * container of every existing export once would be a large surprise cost.
 */
export function scopeCovers(previous: WalkScope | undefined, current: WalkScope): boolean {
    if (!previous) {
        return true;
    }

    const was = previous.dateRange;
    const now = current.dateRange;
    if (was?.after && !(now?.after && now.after >= was.after)) {
        return false;
    }
    if (was?.before && !(now?.before && now.before <= was.before)) {
        return false;
    }

    if (previous.paths) {
        if (!current.paths) {
            return false;
        }
        const old: ContainerSelection = { ids: previous.paths };
        if (!current.paths.every((path) => isPathSelected(path.split("/"), old))) {
            return false;
        }
    }
    return true;
}

/** True when a module's selection excludes everything (nothing to export). */
export function selectsNothing(selection: ContainerSelection | undefined): boolean {
    return selection !== undefined && selection !== "all" && selection.ids.length === 0;
}

/** True when the range has at least one bound set. */
export function hasDateRange(range: DateRange): boolean {
    return Boolean(range.after || range.before);
}

/** A plan with no date bounds and every module set to "all". */
export function isEmptyPlan(plan: ExportPlan): boolean {
    if (Object.values(plan.dateRanges).some((r) => r && hasDateRange(r))) {
        return false;
    }
    return Object.values(plan.selection).every((s) => !s || s === "all");
}
